import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";

export default function Collections({ collections }) {
  if (!collections?.length) return null;
  const [a, b, c] = collections;

  return (
    <section
      id="collections"
      data-testid="collections-section"
      className="relative py-24 md:py-32 bg-black px-6 md:px-12"
    >
      <div className="mb-14">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-3">
          01 — Departures
        </p>
        <h2 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter text-white">
          Collections
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(0,1fr)]">
        {a && (
          <FadeUp className="md:col-span-8 md:row-span-2">
            <CollectionCard collection={a} tall />
          </FadeUp>
        )}
        {b && (
          <FadeUp delay={0.1} className="md:col-span-4">
            <CollectionCard collection={b} />
          </FadeUp>
        )}
        {c && (
          <FadeUp delay={0.2} className="md:col-span-4">
            <CollectionCard collection={c} />
          </FadeUp>
        )}
      </div>
    </section>
  );
}

function CollectionCard({ collection, tall }) {
  return (
    <motion.div
      data-testid={`collection-card-${collection.id}`}
      className={`group relative w-full overflow-hidden border border-white/10 cursor-pointer ${
        tall ? "h-[60vh] md:h-full min-h-[520px]" : "h-[38vh] min-h-[280px]"
      }`}
      whileHover="hover"
    >
      {/* Base image */}
      <motion.img
        src={collection.image}
        alt={collection.title}
        className="absolute inset-0 w-full h-full object-cover saturate-[0.5]"
        variants={{ hover: { scale: 1.06 } }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      />
      {/* Reveal image with mask */}
      <motion.div
        className="absolute inset-0 overflow-hidden"
        variants={{ hover: { clipPath: "inset(0% 0% 0% 0%)" } }}
        initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <img
          src={collection.cover}
          alt={`${collection.title} cover`}
          className="w-full h-full object-cover saturate-[0.6]"
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />

      <div className="absolute top-6 left-6">
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/80 border border-white/25 px-3 py-1">
          {collection.season}
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between">
        <div>
          <h3 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white mb-2">
            {collection.title}
          </h3>
          <p className="text-steel-light text-sm max-w-xs">{collection.tagline}</p>
        </div>
        <motion.div
          className="w-12 h-12 rounded-full border border-white/40 flex items-center justify-center flex-shrink-0"
          variants={{ hover: { backgroundColor: "#ffffff", rotate: 45 } }}
          transition={{ duration: 0.4 }}
        >
          <ArrowUpRight className="w-5 h-5 text-white group-hover:text-black transition-colors duration-300" />
        </motion.div>
      </div>
    </motion.div>
  );
}
